import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useQuery, useQueryClient, useQueries } from 'react-query';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { appRoutes } from '@/routes';
import { Container, Alert, Spinner, FormGroup } from 'reactstrap';
import { Input as CustomInput } from '@components/Input';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button as PrimeButton } from 'primereact/button';
import { Menu } from 'primereact/menu';
import firebase from '../../config/firebase.config';
import { useUserCompanies } from '../../hooks/useUserCompanies';
import { fetchUserTeamsForCompany } from '../../services/userMembership';
import { getTeamMemberDetails } from '../../services/teamMemberDetails';
import { queryKeys } from '../../config/queryKeys';
import { Text, fontWeightEnum, textVariantsEnum } from '@/components/Text';
import styles from './TeamList.module.scss';
import { Dropdown, borderedSelectStyles } from '@/components/Dropdown';
import { ButtonComponent } from '@/components/Button/Button.component';
import { buttonVariants } from '@/components/Button';
import { AddTeamModal } from '@/views/Teams/components/TeamsTab/AddTeamModal.component';
import { useEmployeePageCompanyTeamUrlSync } from '@/hooks/useEmployeePageUrlSync';
import { mergeEmployeePageFiltersIntoSearch } from '@/utils/employeePageUrlFilters';

const database = firebase.firestore();

function countActiveMembers(memberDetailsMap) {
  return Object.entries(memberDetailsMap || {}).filter(
    ([, d]) => d && d.deleted !== true
  ).length;
}

export const TeamListComponent = () => {
  const history = useHistory();
  const location = useLocation();
  const queryClient = useQueryClient();
  const authState = useSelector((state) => ({ ...state.auth }));
  const userProfile = authState.profile;
  const companyList = userProfile?.companyList || [];
  const userUID = userProfile?.id;
  const activeCompanyId = userProfile?.activeCompany?.companyId;
  const userRole = userProfile?.role || '';
  const showAdminActions = userRole === 'Admin';

  const menuRef = useRef(null);
  const noopSetSelectedTeam = useCallback(() => {}, []);

  const [selectedCompany, setSelectedCompany] = useState(null);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [teamModalOpen, setTeamModalOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [menuRow, setMenuRow] = useState(null);

  const addTeamDefaultCompanyId = useMemo(
    () => selectedCompany?.value ?? activeCompanyId,
    [selectedCompany?.value, activeCompanyId]
  );

  const {
    companyOptions,
    loading: loadingCompanies,
    error: companiesError,
  } = useUserCompanies(userUID);

  const {
    data: teams = [],
    isLoading: loadingTeams,
    isError: teamsQueryError,
    error: teamsErrorObj,
  } = useQuery({
    queryKey: queryKeys.teamList.teamsForCompany(
      userUID,
      selectedCompany?.value
    ),
    queryFn: async () => {
      const res = await fetchUserTeamsForCompany(
        userUID,
        selectedCompany.value
      );
      if (!res.success) {
        throw new Error(res.error || 'Failed to load teams');
      }
      return res.data || [];
    },
    enabled: Boolean(userUID && selectedCompany?.value),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const teamOptions = useMemo(
    () =>
      teams.map((t) => ({
        label: t.teamName || t.teamId,
        value: t.teamId,
      })),
    [teams]
  );

  useEmployeePageCompanyTeamUrlSync({
    companyOptions,
    teamOptions,
    selectedCompany,
    selectedTeam: null,
    setSelectedCompany,
    setSelectedTeam: noopSetSelectedTeam,
    history,
    location,
    defaultCompanyId: activeCompanyId,
  });

  const memberQueries = useQueries(
    teams.map((t) => ({
      queryKey: queryKeys.teamMemberDetails.detail(t.teamId),
      queryFn: () => getTeamMemberDetails(database, t.teamId),
      enabled: Boolean(userUID && selectedCompany?.value && t.teamId),
      staleTime: 2 * 60 * 1000,
      refetchOnWindowFocus: false,
    }))
  );

  const tableRows = useMemo(() => {
    return teams.map((t, i) => {
      const q = memberQueries[i];
      const map = q?.data;
      const loadingRow = q?.isLoading;
      const err = q?.isError;
      return {
        teamId: t.teamId,
        teamName: t.teamName || t.teamId,
        memberCount: loadingRow || err ? null : countActiveMembers(map),
        _loading: loadingRow,
      };
    });
  }, [teams, memberQueries]);

  const filteredRows = useMemo(() => {
    const term = (searchTerm || '').trim().toLowerCase();
    if (!term) return tableRows;
    return tableRows.filter((row) =>
      (row.teamName || '').toLowerCase().includes(term)
    );
  }, [tableRows, searchTerm]);

  const loadingMemberDetails =
    teams.length > 0 && memberQueries.some((q) => q.isLoading);

  useEffect(() => {
    const isAdmin =
      userProfile?.activeCompany?.role === 'Admin' ||
      companyList.some((c) => c.role === 'Admin');
    if (!isAdmin && !authState.loading) {
      setError('Access denied. Only Admin users can view department list.');
    }
  }, [userProfile, companyList, authState.loading]);

  useEffect(() => {
    if (!selectedCompany?.value) {
      setSearchTerm('');
    }
  }, [selectedCompany?.value]);

  const invalidateTeamListData = useCallback(() => {
    queryClient.invalidateQueries(
      queryKeys.teamList.teamsForCompany(userUID, selectedCompany?.value)
    );
    teams.forEach((t) => {
      queryClient.invalidateQueries(
        queryKeys.teamMemberDetails.detail(t.teamId)
      );
    });
  }, [queryClient, userUID, selectedCompany?.value, teams]);

  const handleTeamCreatedOrUpdated = useCallback(() => {
    invalidateTeamListData();
  }, [invalidateTeamListData]);

  const onActionsButtonClick = (e, rowData) => {
    e.stopPropagation();
    setMenuRow(rowData);
    menuRef.current?.toggle(e);
  };

  const openEditTeamName = useCallback(() => {
    menuRef.current?.hide();
    if (!menuRow?.teamId) return;
    setEditingTeam({
      teamId: menuRow.teamId,
      teamName: menuRow.teamName,
    });
    setTeamModalOpen(true);
  }, [menuRow]);

  const actionsMenuModel = useMemo(
    () => [
      {
        label: 'Edit',
        icon: 'pi pi-pencil',
        command: openEditTeamName,
      },
    ],
    [openEditTeamName]
  );

  const goToEmployeeListForTeam = useCallback(
    (row) => {
      if (!selectedCompany?.value || !row?.teamId) return;
      history.push({
        pathname: appRoutes().employeeList,
        search: mergeEmployeePageFiltersIntoSearch(location.search, {
          companyId: selectedCompany.value,
          teamId: row.teamId,
        }),
      });
    },
    [history, location.search, selectedCompany?.value]
  );

  const teamsErrorMessage =
    teamsQueryError && teamsErrorObj
      ? teamsErrorObj.message || 'Failed to load teams'
      : null;

  const isAdminUser =
    userProfile?.activeCompany?.role === 'Admin' ||
    companyList.some((c) => c.role === 'Admin');

  if (error && !isAdminUser) {
    return (
      <Container className="mt-4">
        <Alert color="danger">{error}</Alert>
      </Container>
    );
  }

  return (
    <>
      <div className={styles.employeeListPage}>
        <div className={styles.employeeListContainer}>
          <div className={styles.employeeListHeader}>
            <div className={styles.employeeListHeaderTextContainer}>
              <Text
                variant={textVariantsEnum.Header}
                fontWeight={fontWeightEnum.Normal}
              >
                Department List
              </Text>
              <Text
                variant={textVariantsEnum.Text_xs}
                fontWeight={fontWeightEnum.Normal}
                color={'gray-600'}
              >
                Departments you belong to in the selected company
              </Text>
            </div>
            <div className={styles.employeeListHeaderActions}>
              {showAdminActions ? (
                <>
                  <ButtonComponent
                    text="Invite Members"
                    onClick={() =>
                      history.push({
                        pathname: appRoutes().addEmployee,
                        search: mergeEmployeePageFiltersIntoSearch(
                          location.search,
                          {
                            companyId: selectedCompany?.value ?? '',
                            teamId: '',
                          }
                        ),
                      })
                    }
                    variant={buttonVariants.rounded}
                  />
                  <ButtonComponent
                    text="Add department"
                    onClick={() => {
                      setEditingTeam(null);
                      setTeamModalOpen(true);
                    }}
                    variant={buttonVariants.rounded}
                  />
                </>
              ) : null}
            </div>
          </div>
          <div className={styles.employeeListDropdownContainer}>
            <Dropdown
              label="Company *"
              value={selectedCompany}
              selectStyles={borderedSelectStyles}
              options={companyOptions}
              onChange={(option) => {
                setSelectedCompany(option);
                setError(null);
              }}
              placeholder={
                loadingCompanies ? 'Loading companies...' : 'Select Company'
              }
            />
          </div>
          <div className={styles.employeeListTableContainer}>
            <Menu model={actionsMenuModel} popup ref={menuRef} />
            {(error || companiesError || teamsErrorMessage) && (
              <Alert color="danger" className="mb-4">
                {error || companiesError || teamsErrorMessage}
              </Alert>
            )}

            {selectedCompany && teams.length > 0 && !loadingTeams && (
              <FormGroup className="mb-3">
                <CustomInput
                  label="Search departments"
                  id="department-list-search"
                  type="text"
                  placeholder="Search by department name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm.trim() ? (
                  <small className="text-muted">
                    Showing {filteredRows.length} of {tableRows.length} teams
                  </small>
                ) : null}
              </FormGroup>
            )}

            {loadingTeams || loadingMemberDetails ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
                <p className="mt-2">Loading teams...</p>
              </div>
            ) : selectedCompany && teams.length === 0 ? (
              <Alert color="info">
                No teams found for the selected company.
              </Alert>
            ) : selectedCompany && filteredRows.length === 0 ? (
              <Alert color="info">No teams match your search.</Alert>
            ) : selectedCompany && filteredRows.length > 0 ? (
              <div className={styles.employeeListTable}>
                <DataTable
                  className="prime-react-table"
                  value={filteredRows}
                  responsiveLayout="scroll"
                  paginator
                  paginatorClassName="prime-react-paginator"
                  rows={10}
                  rowsPerPageOptions={[10, 25, 50]}
                  emptyMessage="No teams found"
                >
                  <Column
                    field="teamName"
                    header="Name"
                    sortable
                    style={{ minWidth: '180px' }}
                    body={(row) => (
                      <span
                        role="presentation"
                        className={styles.rowNavHit}
                        onClick={() => goToEmployeeListForTeam(row)}
                      >
                        {row.teamName}
                      </span>
                    )}
                  />
                  <Column
                    header="Members"
                    body={(row) =>
                      row._loading ? (
                        <span className="text-muted">…</span>
                      ) : (
                        <span
                          role="presentation"
                          className={styles.rowNavHit}
                          onClick={() => goToEmployeeListForTeam(row)}
                        >
                          {row.memberCount ?? '—'}
                        </span>
                      )
                    }
                    style={{ minWidth: '100px' }}
                  />
                  <Column
                    header="Actions"
                    body={(rowData) => (
                      <PrimeButton
                        icon="pi pi-ellipsis-v"
                        className="p-button-text p-button-rounded"
                        onClick={(e) => onActionsButtonClick(e, rowData)}
                        aria-label="Actions"
                      />
                    )}
                    style={{ width: '80px' }}
                  />
                </DataTable>
              </div>
            ) : (
              <Alert
                style={{
                  backgroundColor: '#1F74EC',
                }}
              >
                Please select a company to view teams.
              </Alert>
            )}
          </div>
        </div>
      </div>
      <AddTeamModal
        isVisible={teamModalOpen}
        onClose={() => {
          setTeamModalOpen(false);
          setEditingTeam(null);
          setMenuRow(null);
        }}
        callback={handleTeamCreatedOrUpdated}
        defaultCompanyId={addTeamDefaultCompanyId}
        companyId={editingTeam ? selectedCompany?.value : undefined}
        teamId={editingTeam?.teamId}
        initialTeamName={editingTeam?.teamName}
      />
    </>
  );
};
